import type { ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { spreadLabels } from './LineChart';
import {
  CHART_MINIMUM,
  LABEL_TEXT,
  TICK_TEXT,
  VALUE_TEXT,
  chartFill,
  roundedShares,
  sparseLabels,
  thinTick,
  type ChartSeries,
} from './scale';

export type StackedPart = { id: string; label: ReactNode; series: ChartSeries };

export type StackedColumnsProps = {
  /**
   * The parts, bottom first. Put the story part first: on the baseline it is the one readers can
   * compare across columns. Two to five parts, in the same order in every column.
   */
  parts: StackedPart[];
  /** The periods, oldest first; `counts` in the order of `parts`. */
  periods: Array<{ id: string; label: ReactNode; counts: number[] }>;
  formatShare: (share: number) => string;
  /** Written in a column left out for having too few ads ("few ads"). */
  tooFew: ReactNode;
  /** `auto` (default) switches to the narrow form in a narrow container: the shares move from beside the last column to a key under the chart. */
  layout?: ChartLayout;
  /** One sentence with the story part's first and last share, for screen readers. */
  description: string;
  className?: string;
};

/**
 * 100% columns over time: how the mix shifts month by month. The story part sits on the baseline;
 * the last column's shares are written beside it, so no legend is needed. Periods under 30 ads are
 * left out (an empty column), and the source line says so.
 */
const GUTTER: Record<ChartLayout, string> = { auto: 'mr-0 @md:mr-32', narrow: 'mr-0', wide: 'mr-32' };

export function StackedColumns({
  parts,
  periods,
  formatShare,
  tooFew,
  layout = 'auto',
  description,
  className,
}: StackedColumnsProps) {
  devWarn(parts.length > 5, `StackedColumns has ${parts.length} parts; five at most.`);
  const shares = periods.map((p) => {
    const total = p.counts.reduce((s, v) => s + v, 0);
    return total >= CHART_MINIMUM.rank ? roundedShares(p.counts) : null;
  });
  const last = [...shares].reverse().find((s) => s != null);
  // Where each part's label sits beside the last column: the middle of its segment, pushed apart.
  const mids: number[] = [];
  if (last) {
    let base = 0;
    for (const s of last) {
      mids.push(100 - (base + s / 2));
      base += s;
    }
  }
  const labelY = spreadLabels(mids, 12);
  const showLabel = sparseLabels(periods.length);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="flex gap-2">
        <div className="relative h-48 w-10 shrink-0" aria-hidden="true">
          {[0, 50, 100].map((t) => (
            <span key={t} className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)} style={{ bottom: `${t}%` }}>
              {formatShare(t / 100)}
            </span>
          ))}
        </div>
        <div className={cx('relative flex h-48 flex-1 gap-1 @md:gap-2', GUTTER[layout])}>
          {periods.map((p, i) => {
            const s = shares[i];
            return (
              <div key={p.id} className="flex h-full flex-1 flex-col-reverse gap-px">
                {s ? (
                  s.map((share, k) =>
                    share > 0 ? (
                      <span
                        key={parts[k].id}
                        className={cx(
                          'block w-full origin-bottom rounded-jc-info motion-safe:animate-jc-grow-y',
                          chartFill(parts[k].series),
                        )}
                        style={{ height: `${share}%` }}
                      />
                    ) : null,
                  )
                ) : (
                  <span className="flex h-full items-end justify-center rounded-jc-info border border-dashed border-jc-chart-grid pb-1 text-center text-xs text-jc-ink-secondary">
                    {tooFew}
                  </span>
                )}
              </div>
            );
          })}
          {last ? (
            <div className={cx('absolute inset-y-0 left-full w-32 pl-3', WIDE_ONLY[layout])} aria-hidden="true">
              {parts.map((part, k) => (
                <span
                  key={part.id}
                  className={cx('absolute flex -translate-y-1/2 items-baseline gap-1.5 whitespace-nowrap', LABEL_TEXT)}
                  style={{ top: `${labelY[k]}%` }}
                >
                  <span className={cx('size-2 shrink-0 self-center rounded-full', chartFill(part.series))} />
                  {part.label}
                  <span className={VALUE_TEXT}>{formatShare(last[k] / 100)}</span>
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className={cx('ml-12 flex gap-1 @md:gap-2', GUTTER[layout])} aria-hidden="true">
        {periods.map((p, i) => (
          <span
            key={p.id}
            className={cx('flex-1 text-center whitespace-nowrap', TICK_TEXT, thinTick(i, periods.length))}
          >
            {showLabel[i] ? p.label : null}
          </span>
        ))}
      </div>
      {last ? (
        <div
          className={cx('flex flex-wrap gap-x-4 gap-y-1 pt-1 type-meta text-jc-ink-secondary', NARROW_ONLY[layout])}
          aria-hidden="true"
        >
          {parts.map((part, k) => (
            <span key={part.id} className="inline-flex items-center gap-1.5">
              <span className={cx('size-2 rounded-full', chartFill(part.series))} />
              {part.label}
              <span className={VALUE_TEXT}>{formatShare(last[k] / 100)}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
